import { lazy, ComponentType } from 'react';
import { logger } from '@shared/logging/logger';

/**
 * Typesafe React.lazy wrapper with dynamic chunk load retry mechanism.
 * Mitigates "Failed to fetch dynamically imported module" errors by retrying the fetch.
 * If all retries fail, it automatically triggers a hard reload to fetch the new site assets.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  retriesLeft = 2,
  interval = 1000
): React.LazyExoticComponent<T> {
  return lazy(() =>
    new Promise<{ default: T }>((resolve, reject) => {
      componentImport()
        .then(resolve)
        .catch((error) => {
          logger.warn('lazy_with_retry', `Failed to load dynamic chunk. Retries remaining: ${retriesLeft}. Error: ${error?.message || error}`);
          
          if (retriesLeft <= 0) {
            logger.error('lazy_with_retry', 'All retries for dynamic chunk load failed. Initiating window reload as fallback...');
            if (typeof window !== 'undefined') {
              window.location.reload();
            }
            reject(error);
            return;
          }

          setTimeout(() => {
            // First retry attempt
            componentImport()
              .then(resolve)
              .catch((err) => {
                logger.warn('lazy_with_retry', `Retry attempt failed. Retries remaining: ${retriesLeft - 1}. Error: ${err?.message || err}`);
                
                if (retriesLeft - 1 <= 0) {
                  logger.error('lazy_with_retry', 'All chunk retries exhausted. Reloading page...');
                  if (typeof window !== 'undefined') {
                    window.location.reload();
                  }
                  reject(err);
                  return;
                }

                // Final retry attempt
                setTimeout(() => {
                  componentImport()
                    .then(resolve)
                    .catch((finalErr) => {
                      logger.error('lazy_with_retry', 'Final retry failed. Forcing page refresh with clean cache...');
                      if (typeof window !== 'undefined') {
                        window.location.reload();
                      }
                      reject(finalErr);
                    });
                }, interval);
              });
          }, interval);
        });
    })
  );
}

export default lazyWithRetry;
